import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography, Dialog, IconButton, OutlinedInput } from '@mui/material';
import { Close } from '@mui/icons-material';
import * as Sentry from '@sentry/react';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white,
  gray80,
  gray60,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { userLimitChangeSchema } from '../../../../validation/company';
import type { UserLimitChangeFormType } from '../../../../types/company';
import { userLimitMax } from '../../../../constants/enum';

import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import MetaTitle from '../../../utils/MetaTitle';
import SideBar from '../../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import { useAdminPageAccessControl } from '../../../../hooks/useAdminPageAccessControl';
import { getCompany } from '../../../../api/firebase/firestore/company/read';
import { updateCompanyUserLimit } from '../../../../api/firebase/firestore/company/update';
import { getUsersByCompanyId } from '../../../../api/firebase/firestore/user/read';
import { boldMd, regularLg, regularXl } from '../../../../constants/location';
import { AdminNoPermissionPage } from '../../../uiParts/parts/AdminNoPermission';

const styles = {
  pageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  modalContainer: {
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    padding: '40px',
    width: '480px',
    boxSizing: 'border-box',
    position: 'relative',
    '@media screen and (max-width: 960px)': {
      padding: '32px 16px',
      width: '100%',
      maxWidth: '480px',
    },
  },
  modalHeader: {
    marginBottom: '24px',
    '@media screen and (max-width: 960px)': {
      marginBottom: '16px',
    },
  },
  modalTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    textAlign: 'left',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px',
    },
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  backButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  backButtonText: {
    ...boldMd,
    color: gray80,
  },
  submitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  submitButtonText: {
    ...boldMd,
    color: 'white',
  },
  formLabel: {
    fontSize: '14px',
    color: gray80,
  },
  required: {
    color: red50,
    fontSize: '14px',
    marginLeft: '4px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  currentLimitInput: {
    width: '80px',
    '& .MuiOutlinedInput-input': {
      padding: '10px 8px',
      textAlign: 'center',
      fontSize: '16px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
    },
    backgroundColor: '#F5F5F5',
  },
  newLimitInput: {
    width: '80px',
    '& .MuiOutlinedInput-input': {
      padding: '10px 8px',
      textAlign: 'center',
      fontSize: '16px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
  },
  arrow: {
    fontSize: '20px',
    color: gray80,
  },
  unit: {
    fontSize: '14px',
    color: gray100,
  },
  errorText: {
    color: red50,
    fontSize: '12px',
    marginTop: '4px',
    textAlign: 'left',
  },
  dialogPaper: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  dialogIcon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogIconButton: {
    padding: 0,
  },
  dialogButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  dialogCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  dialogConfirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
  },
  errorMessage: {
    fontSize: '16px',
    color: gray100,
    textAlign: 'center',
  },
};

const CompanyUserLimitChange: React.FC = (): JSX.Element => {
  const { companyId } = useParams<{ companyId: string }>();
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [currentLimit, setCurrentLimit] = useState<number | null>(null);
  const [currentUserCount, setCurrentUserCount] = useState<number>(0);
  const [companyNotFound, setCompanyNotFound] = useState(false);
  const { user, noPermission, isChecking } = useAdminPageAccessControl('user limit change page');
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<UserLimitChangeFormType>({
    resolver: yupResolver(userLimitChangeSchema),
    defaultValues: {
      newLimit: 0,
      note: '',
    },
  });

  const newLimit = watch('newLimit');

  const isButtonDisabled = (): boolean => {
    if (currentLimit === null) return true;
    return newLimit === currentLimit;
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const validateCurrentUserCount = (): boolean => {
    if (newLimit < currentUserCount) {
      setError('newLimit', {
        type: 'manual',
        message: `現在所属している担当者数（${currentUserCount}人）より少なく設定できません`,
      });
      return false;
    }
    return true;
  };

  const handleConfirmClick = async (): Promise<void> => {
    await handleSubmit(() => {
      if (validateCurrentUserCount()) {
        setDialogOpen(true);
      }
    })();
  };

  const handleDialogClose = (): void => {
    setDialogOpen(false);
  };

  const handleUserLimitChange = async (): Promise<void> => {
    if (!companyId || !user?.uid || currentLimit === null) return;

    try {
      setSubmitting(true);
      const note = watch('note');

      await updateCompanyUserLimit(companyId, currentLimit, newLimit, note, user.uid);

      navigate(`/admin/user_limit_change/${companyId}/complete`, {
        state: { fromUserLimitChange: true },
      });
    } catch (error: unknown) {
      Sentry.captureException(error);
      setErrorText('担当者数変更に失敗しました。時間をおいて再度お試しください。');
      setOpen(true);
      setDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchCompanyData = async (): Promise<void> => {
      // アクセス制御はフックに委譲
      if (isChecking || noPermission || !user) return;

      if (!companyId || dataFetched) return;

      try {
        const companyData = await getCompany(companyId);

        if (!companyData) {
          Sentry.captureMessage('Company not found for user limit change', {
            level: 'warning',
            extra: { companyId },
          });
          setCompanyNotFound(true);
          setLoading(false);
          return;
        }

        const limit = typeof companyData.user_limit === 'number' ? companyData.user_limit : 1;
        setCurrentLimit(limit);
        setValue('newLimit', limit);

        // 現在の所属担当者数を取得
        const users = await getUsersByCompanyId(companyId);
        setCurrentUserCount(users.length);

        setDataFetched(true);
      } catch (error: unknown) {
        Sentry.captureException(error);
        setErrorText('企業情報の取得に失敗しました。');
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [isChecking, noPermission, user, navigate, setValue, dataFetched, companyId]);

  // 権限がない場合
  if (noPermission) {
    return <AdminNoPermissionPage companyId={companyId} />;
  }

  // ローディング中はフォームを描画しない
  if (isChecking || loading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle="担当者数変更" />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box
          sx={[
            layoutStyles.pageContent,
            sideBarCollapsed && layoutStyles.pageContentCollapsed,
            layoutStyles.pageContentNoFooter,
          ]}
        >
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  // 企業が存在しない場合
  if (companyNotFound) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle="担当者数変更" />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box
          sx={[
            layoutStyles.pageContent,
            sideBarCollapsed && layoutStyles.pageContentCollapsed,
            layoutStyles.pageContentNoFooter,
          ]}
        >
          <Box sx={[styles.pageContainer, layoutStyles.fullHeightContent]}>
            <Box sx={styles.modalContainer}>
              <Box sx={styles.errorContainer}>
                <Typography sx={styles.errorMessage}>
                  指定された企業が見つかりませんでした。
                </Typography>
                <Button
                  onClick={(): void => navigate('/admin/company')}
                  sx={[primaryButtonBackgroundColor, { width: '200px', height: '44px' }]}
                  variant="contained"
                >
                  企業一覧に戻る
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="担当者数変更" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          layoutStyles.pageContentNoFooter,
        ]}
      >
        <Box sx={[styles.pageContainer, layoutStyles.fullHeightContent]}>
          <Box sx={styles.modalContainer}>
            <Box sx={styles.modalHeader}>
              <Typography sx={styles.modalTitle}>担当者数変更</Typography>
            </Box>

            <Box sx={{ marginBottom: '24px' }}>
              <Box sx={{ display: 'flex', gap: '24px', marginBottom: '8px' }}>
                <Typography sx={styles.formLabel}>現在の担当者数</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={styles.formLabel}>変更後担当者数</Typography>
                  <Typography sx={styles.required}>必須</Typography>
                </Box>
              </Box>
              <Box sx={styles.inputRow}>
                <OutlinedInput value={currentLimit ?? ''} disabled sx={styles.currentLimitInput} />
                <Typography sx={styles.arrow}>→</Typography>
                <Controller
                  name="newLimit"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <OutlinedInput
                      {...field}
                      type="number"
                      sx={{
                        ...styles.newLimitInput,
                        ...(errors.newLimit && {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: red50,
                          },
                        }),
                      }}
                      inputProps={{ min: 1, max: userLimitMax }}
                      onChange={(e): void => {
                        const value = e.target.value === '' ? 0 : Number(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
                <Typography sx={styles.unit}>人</Typography>
              </Box>
              {errors.newLimit && (
                <Typography sx={styles.errorText}>{errors.newLimit.message}</Typography>
              )}
            </Box>

            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.note)}
                  formMb="0px"
                  helperText={errors.note?.message}
                  labelText="備考"
                  requiredText="必須"
                  placeholder=""
                  width="100%"
                  inputWidth="100%"
                  multiline
                  rows={4}
                  height="auto"
                />
              )}
              control={control}
              name="note"
            />

            <Box sx={styles.buttonWrapper}>
              <Button sx={styles.backButton} onClick={handleBack}>
                <Typography sx={styles.backButtonText}>戻る</Typography>
              </Button>
              <Button
                disabled={isButtonDisabled()}
                onClick={handleConfirmClick}
                sx={styles.submitButton}
                variant="contained"
              >
                <Typography sx={styles.submitButtonText}>確認</Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 確認ダイアログ */}
      <Dialog open={dialogOpen} sx={styles.dialogPaper}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>担当者数変更確認</Typography>
          <IconButton onClick={handleDialogClose} sx={styles.dialogIconButton}>
            <Close sx={styles.dialogIcon} />
          </IconButton>
        </Box>
        <Typography>
          現在の担当者数（{currentLimit}人）から（{newLimit}人）担当者数を変更します。
          <br />
          実行してもよろしいですか？
        </Typography>
        <Box sx={styles.dialogButtonWrapper}>
          <Button sx={styles.dialogCancelButton} onClick={handleDialogClose} disabled={submitting}>
            <Typography sx={styles.backButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.dialogConfirmButton}
            onClick={handleUserLimitChange}
            disabled={submitting}
            variant="contained"
          >
            <Typography sx={styles.submitButtonText}>
              {submitting ? '変更中...' : '変更する'}
            </Typography>
          </Button>
        </Box>
      </Dialog>

      <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
    </Box>
  );
};

export default CompanyUserLimitChange;
